import React, { useContext } from 'react'
import { IdentityContext } from '#src/containers/Identity'
import { Avatar, Button, Logo } from '@deal/components'
import { Conversation, Composer, ChatContainerProps } from '@deal/chat-firebase'
import { Link } from 'react-router-dom'
import useClickOutside from '#src/hooks/useClickOutside'
import {
  useChatExpertQuery,
  useChatExpertByDepartmentQuery,
  useStartExpertConsumerConversationMutation
} from '#src/generated/graphql'
import CloseIcon from './close.svg'
// @ts-ignore
import Scrolllock from 'react-scrolllock'
import { DepartmentContext } from '#src/context/department'
import CardBackgroundImage from './card-bg.jpg'
import BackgroundImage from '../BackgroundImage'
import classnames from 'classnames'
import styles from './styles.css'
import { generateCreateUrlFromCategory } from '#src/services/path'

export enum ChatTabDisplay {
  // The user has explicitly closed the chat
  Collapsed,
  // The chat is half-way expanded, showing the latest messages
  Previewed,
  // The chat is expanded, taking over the full screen
  Expanded
}

interface ChatTabProps extends ChatContainerProps {
  expertId?: string
  display: ChatTabDisplay
  onDisplayChanged: (display: ChatTabDisplay) => void
}

// TODO: Consolidate business logic between ChatTab & ChatSidebar
const ChatTab: React.SFC<ChatTabProps> = ({
  display,
  onDisplayChanged,
  expertId,
  recipientIds,
  conversationId,
  ...props
}) => {
  const { myself } = useContext(IdentityContext)
  const { department } = useContext(DepartmentContext)
  const [
    startExpertConsumerConversation,
    { data: startConversationData }
  ] = useStartExpertConsumerConversationMutation()

  // Get an expert by ID, if provided
  const { data: dataByExpert } = useChatExpertQuery({
    variables: {
      businessUserId: expertId
    },
    skip: expertId === undefined
  })

  // Otherwise, fall back to fetching the recommended expert for the active department
  const { data: dataByDepartment } = useChatExpertByDepartmentQuery({
    variables: {
      id: department && department.id
    },
    skip: !department || expertId !== undefined
  })

  const recommendedDepartmentExpert =
    dataByDepartment && dataByDepartment.department.topLevelCategory.recommendExpertForConsumer
  const recommendedDepartmentConversationId =
    recommendedDepartmentExpert &&
    recommendedDepartmentExpert.existingLead &&
    recommendedDepartmentExpert.existingLead.conversation.id

  const expert = dataByExpert
    ? dataByExpert.businessUser
    : recommendedDepartmentExpert
    ? recommendedDepartmentExpert.expert
    : null
  const expertProfile = expert && expert.expertProfile
  const expertPhoto = expertProfile && expertProfile.profilePhoto && expertProfile.profilePhoto.url
  const expertName = expert && expert.displayName

  // If we are starting a conversation via the mutation, we will fall back to the newly created ID.
  // These should be the same, assuming whatever is passing in `props.conversationId` updates correctly.
  const newConversationId =
    startConversationData &&
    startConversationData.startExpertConsumerConversation.conversation &&
    startConversationData.startExpertConsumerConversation.conversation.id
  const derivedConversationdId =
    conversationId || newConversationId || recommendedDepartmentConversationId

  const guestChatEnabled = !myself || !expert

  const handleConversationStarted = () => {
    if (myself && expert) {
      startExpertConsumerConversation({
        variables: {
          input: {
            consumerId: myself.id,
            expertId: expert.id
          }
        }
      })
    }
  }

  // Close the chat when the close button is clicked, and prevent it from bubbling to the
  //  following event handlers
  const handleCloseClicked = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDisplayChanged(ChatTabDisplay.Collapsed)
  }

  // In the previewed state, disable the expert profile link. Instead, let the click event
  //   bubble up to the header click handler
  const handleExpertLinkClicked = (e: React.MouseEvent) => {
    if (display === ChatTabDisplay.Previewed) {
      e.preventDefault()
    }
  }

  // In the previewed state, clicking the header expands the chat
  // In the expanded state, clicking the header collapses the chat
  const handleHeaderClicked = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (display === ChatTabDisplay.Previewed) {
      onDisplayChanged(ChatTabDisplay.Expanded)
    }

    if (display === ChatTabDisplay.Expanded) {
      onDisplayChanged(ChatTabDisplay.Collapsed)
    }
  }

  // In the previewed state, clicking anywhere in the conversation expands the chat
  const handleChatClicked = () => {
    if (display === ChatTabDisplay.Previewed) {
      onDisplayChanged(ChatTabDisplay.Expanded)
    }
  }

  // In the previewed state, focusing on the composer expands the chat
  const handleComposerFocused = () => {
    if (display === ChatTabDisplay.Previewed) {
      onDisplayChanged(ChatTabDisplay.Expanded)
    }
  }

  // In the previewed state, clicking anywhere outside the chat collapses it
  const chatRef = useClickOutside<HTMLDivElement>(() => {
    if (display === ChatTabDisplay.Previewed) {
      onDisplayChanged(ChatTabDisplay.Collapsed)
    }
  })

  return (
    <>
      <Scrolllock isActive={display !== ChatTabDisplay.Collapsed} />
      <div
        className={classnames(styles.chat, {
          [styles.previewed]: display === ChatTabDisplay.Previewed,
          [styles.expanded]: display === ChatTabDisplay.Expanded
        })}
        onClick={handleChatClicked}
        ref={chatRef}
      >
        <div className={styles.header} onClick={handleHeaderClicked}>
          <CloseIcon className={styles.closeIcon} onClick={handleCloseClicked}></CloseIcon>

          {expert && expertProfile ? (
            <Link
              to={`/experts/${expert.vanityId || expert.id}`}
              className={styles.expertLink}
              onClick={handleExpertLinkClicked}
            >
              <Avatar
                name={expertName!}
                src={expertPhoto || undefined}
                className={styles.expertAvatar}
                size="medium"
              />
              {expertName!}
            </Link>
          ) : (
            <span className={styles.expertLink}>
              <Logo mark="icon" className={styles.expertAvatar} />
              Curated
            </span>
          )}
        </div>
        {expert ? (
          <>
            <Conversation
              recipientIds={recipientIds}
              conversationId={derivedConversationdId}
              enableReadReceipts={display === ChatTabDisplay.Expanded}
              guestChatEnabled={guestChatEnabled}
              {...props}
            />
            <Composer
              recipientIds={recipientIds}
              conversationId={derivedConversationdId}
              guestChatEnabled={guestChatEnabled}
              onConversationStarted={handleConversationStarted}
              onFocus={handleComposerFocused}
            />
          </>
        ) : (
          <>
            <div className={styles.emptyChat}>
              <div className={styles.quizCard}>
                <BackgroundImage
                  src={CardBackgroundImage}
                  sizes="100vw"
                  contentClassName={styles.quizCardHeader}
                >
                  Take a 2 min quiz and get connected with an expert
                </BackgroundImage>
                <div className={styles.quizCardContent}>
                  <p className={styles.quizCardListHeader}>How it works</p>
                  <ol className={styles.quizCardList}>
                    <li>Tell us what you want</li>
                    <li>Meet your expert</li>
                    <li>Find it together</li>
                    <li>Buy and enjoy</li>
                  </ol>
                  <Link
                    to={generateCreateUrlFromCategory({
                      sourceKey: 'chat-tab-empty-state',
                      pathIntent: 'request'
                    })}
                  >
                    <Button size="small">Take quiz</Button>
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default ChatTab
